import React, { useEffect, useRef, useState } from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "../../lib/cn"
import { Button } from "./Button"
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./Command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./Popover"

const normalizeSearchText = (value) => String(value || "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, " ")
  .trim()

/**
 * Reusable SearchableCombobox component using shadcn/ui's Popover and Command.
 * 
 * @param {Object[]} items - Array of objects with { value, label }
 * @param {string} [items[].description] - Optional secondary line shown under the label
 * @param {string} [items[].searchText] - Optional search text override
 * @param {string} value - Selected value
 * @param {function} onChange - Callback when value changes
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Whether the combobox is disabled
 * @param {string} className - Additional classes
 */
const SearchableCombobox = ({
  items = [],
  value,
  onChange,
  placeholder = "Select option...",
  disabled = false,
  className,
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const selectionGuardRef = useRef(false)

  useEffect(() => {
    if (open) {
      selectionGuardRef.current = false
    } else {
      setQuery("")
    }
  }, [open])

  const searchTerms = normalizeSearchText(query).split(" ").filter(Boolean)
  const filteredItems = items.filter((item) => {
    if (searchTerms.length === 0) return true
    const haystack = normalizeSearchText([
      item.searchText,
      item.label,
      item.description,
      item.value,
    ].filter(Boolean).join(" "))
    return searchTerms.every((term) => haystack.includes(term))
  })

  const selectWithMouse = (e, nextValue) => {
    e.preventDefault()
    e.stopPropagation()
    if (selectionGuardRef.current) return
    selectionGuardRef.current = true
    onChange(nextValue)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex min-h-10 h-auto w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-left",
            disabled && "bg-slate-100 text-slate-500",
            className
          )}
        >
          <span className="flex flex-1 flex-col items-start text-left min-w-0">
            {value
              ? (() => {
                  const selectedItem = items.find((item) => item.value === value)
                  if (!selectedItem) return placeholder
                  return (
                    <>
                      <span className="block truncate">{selectedItem.label}</span>
                      {selectedItem.description ? (
                        <span className="block text-xs text-gray-500 truncate max-w-full">{selectedItem.description}</span>
                      ) : null}
                    </>
                  )
                })()
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] max-w-[90vw] p-0" align="start">
        <Command className="min-w-full h-auto" shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={`Search ${placeholder.toLowerCase()}...`}
          />
          <CommandList>
            {filteredItems.length === 0 && (
              <div className="py-6 text-center text-sm text-gray-500">No option found.</div>
            )}
            <CommandGroup>
              {filteredItems.map((item) => (
                <CommandItem
                  key={item.value}
                  value={[item.searchText, item.label, item.description, item.value].filter(Boolean).join(' ')}
                  onSelect={(currentValue) => {
                    if (selectionGuardRef.current) return
                    selectionGuardRef.current = true
                    onChange(item.value)
                    setOpen(false)
                  }}
                  onMouseDown={(e) => selectWithMouse(e, item.value)}
                  onClick={(e) => selectWithMouse(e, item.value)}
                  className={cn(
                    "cursor-pointer",
                    value === item.value && "bg-indigo-50 text-indigo-700 font-medium"
                  )}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === item.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="flex min-w-0 flex-1 flex-col text-left">
                    <span className="break-words">{item.label}</span>
                    {item.description ? (
                      <span className="break-words text-xs text-gray-500">{item.description}</span>
                    ) : null}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default SearchableCombobox
